import { useEffect, useState } from 'react';
import {
  chipLabel,
  todayIsoParis,
  type ActivityQualification,
  type BoardActivity,
  type Employee,
} from '@lemontaigu/staff';
import { useBoard, useBoardPresence } from '@lemontaigu/staff/react';
import { FrDateInput } from '../ui/FrDateInput.js';
import { frDayLabel, shiftDay } from './logic.js';

// Admin quick-edit (operator request 2026-07-23): click a name in the « Jour »
// board's Présence list to set REPOS (board_absence, source='desk' — the CHECK
// constraint has no 'operator' value) and shift assignments (board_activity_presence,
// status='assigned', source='operator' — same write AssignEditor performs, just
// employee-first instead of activity-first) for any day, not only the one on
// screen. Self-contained like EmployeeWeek: its own day nav + Supabase reads/writes;
// DayView's realtime subscription on both tables picks up the change automatically.
// Repos and a shift assignment are mutually exclusive for a given day — setting
// one clears the other.

// Reads go through the BoardStore port (ladder step 6); the writes below still
// name their tables — they move at step 7. The port has no employee filter by
// design (see EmployeeWeek), so the single-day read is filtered here.

export function EmployeeQuickEdit({
  employee,
  color,
  anchorDay,
  activities,
  qualifications,
  onClose,
}: {
  employee: Employee;
  color: string | undefined;
  anchorDay: string;
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
  onClose: () => void;
}) {
  const board = useBoard();
  const boardPresence = useBoardPresence();
  const [editDay, setEditDay] = useState(anchorDay);
  const [absent, setAbsent] = useState(false);
  const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async (key: string) => {
    setLoaded(false);
    setErr('');
    try {
      const [ab, pr] = await Promise.all([board.absences(key, key), board.presence(key, key)]);
      setAbsent(ab.some((r) => r.employeeId === employee.id));
      setAssignedIds(
        new Set(
          pr.filter((r) => r.employeeId === employee.id && r.status === 'assigned').map((r) => r.activityId),
        ),
      );
      setLoaded(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load(editDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDay, employee.id]);

  // REPOS and shift assignment are mutually exclusive. Both directions used to
  // be two sequential writes here, with the second one's error discarded — and
  // in the OPPOSITE order to apps/staff's version of the same rule, so the two
  // failed in opposite ways. One transaction now (step 7 / migration 0055).
  const toggleRepos = async (next: boolean) => {
    setBusy(true);
    setErr('');
    try {
      if (next)
        await boardPresence.markAbsent({
          employeeId: employee.id,
          date: editDay,
          source: 'desk',
          declaredBy: null,
        });
      else await boardPresence.clearAbsence(employee.id, editDay);
      void load(editDay);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActivity = async (activityId: number, next: boolean) => {
    setBusy(true);
    setErr('');
    try {
      if (next)
        await boardPresence.declarePresence({
          activityId,
          employeeId: employee.id,
          date: editDay,
          status: 'assigned',
          source: 'operator',
          declaredBy: null,
        });
      else await boardPresence.withdrawPresence(activityId, employee.id, editDay);
      void load(editDay);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const today = todayIsoParis();
  const activeActivities = activities.filter((a) => a.active);
  const qualifiedIds = new Set(qualifications.filter((q) => q.employeeId === employee.id).map((q) => q.activityId));

  return (
    <div className="empweek-backdrop" onClick={onClose}>
      <div className="empweek eqe-panel" onClick={(e) => e.stopPropagation()}>
        <header className="empweek-head">
          <span className="gantt-chip" style={{ background: color }}>
            {chipLabel(employee)}
          </span>
          <strong>
            {employee.firstName} {employee.lastName}
          </strong>
          <button type="button" className="empweek-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </header>

        <div className="staff-daybar eqe-daybar">
          <button type="button" onClick={() => setEditDay(shiftDay(editDay, -1))} aria-label="Jour précédent">
            ‹
          </button>
          <FrDateInput className="pt-datepicker" value={editDay} onChange={(v) => v && setEditDay(v)} />
          <button type="button" onClick={() => setEditDay(shiftDay(editDay, 1))} aria-label="Jour suivant">
            ›
          </button>
          <span className="staff-daylabel">{frDayLabel(editDay)}</span>
          {editDay !== today && (
            <button type="button" className="staff-today" onClick={() => setEditDay(today)}>
              Aujourd’hui
            </button>
          )}
        </div>

        {err && <p className="auth-err">Erreur : {err}</p>}
        {!loaded && !err && <p className="staff-empty">Chargement…</p>}

        {loaded && (
          <div className="eqe-body">
            <label className="eqe-repos">
              <input type="checkbox" checked={absent} disabled={busy} onChange={(e) => void toggleRepos(e.target.checked)} />
              Repos ce jour
            </label>

            <div className="eqe-activities">
              <span className="board-assign-label">Activités assignées :</span>
              {activeActivities.length === 0 && <span className="board-assign-empty">aucune activité</span>}
              <ul className="eqe-activity-list">
                {activeActivities.map((a) => (
                  <li key={a.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={assignedIds.has(a.id)}
                        disabled={busy}
                        onChange={(e) => void toggleActivity(a.id, e.target.checked)}
                      />
                      {a.label}
                      {!qualifiedIds.has(a.id) && (
                        <span className="board-assign-warn" title="Non qualifié·e pour cette activité">
                          {' '}
                          ⚠
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
